<template>
    <BModal ref="modal" centered scrollable no-close-on-backdrop
        header-class="modal-title" modal-class="no-border-modal" @hidden="onHidden">
        <template #title>
            {{ $t('facilityLicenseModal.title') }}
            <span id="facility-licenses-site" class="d-block font-13 fw-normal text-muted mt-1">{{ facilityName }}</span>
        </template>

        <!-- Activation from the key alone, no type to choose (the backend gives the one of the key) -->
        <form ref="formRef" class="mb-4" novalidate @submit.prevent="submit">
            <hr class="mt-0 mb-3" />
            <h5 id="facility-license-add-title" class="header-title mb-2">{{ $t('facilityLicenseModal.addSection') }}</h5>
            <label for="facility-license-key" class="form-label">{{ $t('facilityLicenseModal.keyLabel') }}</label>
            <div class="d-flex">
                <input id="facility-license-key" ref="keyInput" type="text" class="form-control" :value="key" @input="onKeyInput"
                    :placeholder="$t('facilityLicenseModal.keyPlaceholder')" autocomplete="off" spellcheck="false" required />
                <button type="submit" id="facility-license-add" class="btn btn-primary ms-2 text-nowrap" :disabled="saving">
                    <i class="mdi mdi-plus me-1"></i>{{ $t('facilityLicenseModal.activate') }}
                </button>
            </div>
            <p v-if="error" id="facility-license-error" class="text-danger mt-2 mb-0">{{ error }}</p>
        </form>

        <!-- Licenses of the installation, as the app lists them -->
        <h5 id="facility-licenses-title" class="header-title mb-0">
            {{ $t('facilityLicenseModal.activeSection') }}<template v-if="!loading && !loadError"> ({{ licenses.length }})</template>
        </h5>
        <hr class="mt-2 mb-0" />
        <p v-if="loading" class="mt-3 mb-0">{{ $t('common.loading') }}</p>
        <p v-else-if="loadError" id="facility-licenses-load-error" class="text-danger mt-3 mb-0">{{ loadError }}</p>
        <p v-else-if="licenses.length === 0" id="facility-licenses-empty" class="text-center mt-3 mb-0">{{ $t('facilityLicenseModal.empty') }}</p>
        <table v-else id="facility-licenses" class="table table-centered mb-0">
            <tbody>
                <tr v-for="license in licenses" :key="license.id">
                    <td>
                        <span class="d-block fw-bold text-primary license-key">{{ license.unique_id }}</span>
                        <template v-if="confirmingId === license.id">
                            <span class="d-block license-delete-question">{{ $t('facilityLicenseModal.deleteConfirm') }}</span>
                            <div class="d-flex justify-content-end mt-2">
                                <button type="button" class="btn btn-sm btn-secondary license-delete-cancel" :disabled="deleting" @click="confirmingId = null">{{ $t('common.cancel') }}</button>
                                <button type="button" class="btn btn-sm btn-danger ms-2 license-delete-confirm" :disabled="deleting" @click="deleteLicense(license)">{{ $t('common.delete') }}</button>
                            </div>
                        </template>
                        <template v-else>
                            <span class="d-block license-type">{{ $t('facilityLicenseModal.licenseLabel', { type: license.type }) }}</span>
                            <span class="d-block font-13 fst-italic text-muted license-expiration">{{ $t('facilityLicenseModal.expiresOn', { date: license.expirationDate }) }}</span>
                        </template>
                    </td>
                    <td v-if="canDelete" class="table-action text-end">
                        <a v-if="confirmingId !== license.id" href="javascript:void(0);" class="text-danger license-delete" :title="$t('common.delete')" :aria-label="$t('common.delete')" @click="confirmingId = license.id">
                            <i class="mdi mdi-trash-can font-18"></i>
                        </a>
                    </td>
                </tr>
            </tbody>
        </table>
        <template #footer>
            <div class="d-flex justify-content-end w-100">
                <button type="button" class="btn btn-secondary" @click="close">{{ $t('common.close') }}</button>
            </div>
        </template>
    </BModal>
</template>

<script>
import { BModal } from 'bootstrap-vue-next';
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import { LICENSE_KEY_REGEX } from '@/models/license';

// Licenses of the installation itself, not of a LINK, as the app manages them: listed with their
// type and expiration, activated from their key alone, deleted after a confirmation. Deleting asks
// the right to create installations, the one the app asks to delete an installation.
// show(facilityId) resolves when closed, with true when a license was added or deleted.
export default {
    name: 'FacilityLicenseModal',
    components: {
        BModal
    },
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const modal = ref(null);
        const formRef = ref(null);
        const keyInput = ref(null);
        const facilityName = ref('');
        const licenses = ref([]);
        const loading = ref(false);
        const loadError = ref('');
        const key = ref('');
        const error = ref('');
        const saving = ref(false);
        const confirmingId = ref(null);
        const deleting = ref(false);
        let facilityId = null;
        let changed = false;
        let resolver = null;
        let loadId = 0;

        const canDelete = computed(() => !!userStore.installer?.can_create);

        const load = async () => {
            const id = ++loadId;
            loading.value = true;
            loadError.value = '';
            try {
                const result = await userStore.loadFacilityLicenses(facilityId);
                if (id === loadId) licenses.value = result;
            } catch (message) {
                if (id === loadId) loadError.value = message;
            } finally {
                if (id === loadId) loading.value = false;
            }
        };

        const show = (id) => {
            facilityId = id;
            facilityName.value = userStore.getFacility(id)?.name ?? '';
            changed = false;
            licenses.value = [];
            key.value = '';
            error.value = '';
            saving.value = false;
            confirmingId.value = null;
            deleting.value = false;
            modal.value.show();
            load();
            return new Promise(resolve => { resolver = resolve; });
        };

        const close = () => {
            const resolve = resolver;
            resolver = null;
            modal.value.hide();
            if (resolve) resolve(changed);
        };

        const onHidden = () => {
            if (resolver) close();
        };

        // The app reads the key in upper case
        const onKeyInput = (event) => {
            key.value = event.target.value.toUpperCase();
            event.target.setCustomValidity('');
            error.value = '';
        };

        // Same check as the app's scan: 5 groups of 5 characters, else "Code not recognized"
        const submit = async () => {
            if (saving.value) return;
            key.value = key.value.trim().toUpperCase();
            await nextTick();
            keyInput.value.setCustomValidity(key.value !== '' && !LICENSE_KEY_REGEX.test(key.value) ? t('licenseModal.keyInvalid') : '');
            if (!formRef.value.checkValidity()) {
                formRef.value.reportValidity();
                return;
            }

            saving.value = true;
            error.value = '';
            try {
                const license = await userStore.addFacilityLicense(facilityId, key.value);
                licenses.value.push(license);
                changed = true;
                key.value = '';
                showToast('success', t('facilityLicenseModal.added'));
            } catch (message) {
                error.value = message;
            } finally {
                saving.value = false;
            }
        };

        const deleteLicense = async (license) => {
            if (deleting.value) return;
            deleting.value = true;
            try {
                await userStore.deleteFacilityLicense(facilityId, license);
                licenses.value = licenses.value.filter(l => l.id !== license.id);
                changed = true;
                showToast('success', t('facilityLicenseModal.deleted'));
            } catch (message) {
                showToast('error', message);
            } finally {
                deleting.value = false;
                confirmingId.value = null;
            }
        };

        return {
            modal,
            formRef,
            keyInput,
            facilityName,
            licenses,
            loading,
            loadError,
            key,
            error,
            saving,
            confirmingId,
            deleting,
            canDelete,
            show,
            close,
            onHidden,
            onKeyInput,
            submit,
            deleteLicense,
        };
    }
}
</script>
